/**
 * Step 4: Document Verification (Optional)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, ChevronLeft, Upload, FileText, CheckCircle, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { 
    id: 'business_license', 
    label: 'Business License', 
    description: 'Your state or local business license',
    required: false
  },
  { 
    id: 'sales_tax_certificate', 
    label: 'Sales Tax Certificate', 
    description: 'Certificate of Authority / Sales Tax Permit',
    required: false
  },
  { 
    id: 'food_handling_permit', 
    label: 'Food Handling Permit', 
    description: 'If you sell food items (recommended)',
    required: false
  }
];

export default function DocumentsStep({ data, onComplete, onBack }) {
  const [documents, setDocuments] = useState(data?.documents || []);
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  const handleFileSelect = async (docType, file) => {
    if (!file) return;

    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPEG, PNG, or WebP');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(prev => ({ ...prev, [docType]: true }));

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_URL}/api/retailer/onboarding/documents/upload?document_type=${docType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      const result = await response.json();

      if (response.ok) {
        setDocuments(prev => [
          ...prev.filter(d => d.document_type !== docType),
          {
            document_id: result.document_id,
            document_type: docType,
            file_name: file.name,
            uploaded_at: new Date().toISOString()
          }
        ]);
        toast.success(`${DOCUMENT_TYPES.find(d => d.id === docType)?.label} uploaded!`);
      } else {
        toast.error(result.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const removeDocument = (docType) => {
    setDocuments(prev => prev.filter(d => d.document_type !== docType));
  };

  const getUploadedDoc = (docType) => {
    return documents.find(d => d.document_type === docType);
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/documents/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        onComplete(result);
      } else {
        toast.error(result.detail || 'Failed to continue');
      }
    } catch (error) {
      toast.error('Failed to continue');
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/retailer/onboarding/documents/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Documents saved!');
        onComplete(result);
      } else {
        toast.error(result.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">Documents are optional</p>
          <p className="text-sm text-blue-700">
            Uploading verification documents helps build trust with customers, 
            but you can skip this step and add them later.
          </p>
        </div>
      </div>

      {/* Document upload cards */}
      <div className="space-y-4">
        {DOCUMENT_TYPES.map((docType) => {
          const uploaded = getUploadedDoc(docType.id);
          const isUploading = uploading[docType.id];

          return (
            <div
              key={docType.id}
              className={`border rounded-lg p-4 ${
                uploaded ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FileText className={`w-5 h-5 mt-0.5 ${
                    uploaded ? 'text-emerald-600' : 'text-gray-400'
                  }`} />
                  <div>
                    <Label className="font-medium">{docType.label}</Label>
                    <p className="text-sm text-gray-500">{docType.description}</p>
                    
                    {uploaded && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-emerald-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>{uploaded.file_name}</span>
                        <button
                          type="button"
                          onClick={() => removeDocument(docType.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {!uploaded && (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileSelect(docType.id, e.target.files[0])}
                      disabled={isUploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isUploading}
                      asChild
                    >
                      <span>
                        {isUploading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Upload
                      </span>
                    </Button>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {documents.length === 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleSkip}
              disabled={saving}
              data-testid="skip-documents-btn"
            >
              Skip for Now
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleContinue}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-documents-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save & Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
